import React from 'react';
import {
  Alert,
  Badge,
  Box,
  Chip,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import SyncIcon from '@mui/icons-material/Sync';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import { useAuth } from '../auth/AuthContext';
import { useRealtime } from '../realtime/RealtimeContext';
import config from '../config';
import {
  ResizableColGroup,
  ResizableHeaderCell,
  TableEmptyRow,
  TableLoadingRow,
  useResizableColumns,
} from './table/ResizableTable';

const TIME_WINDOWS = [
  { value: '15m', label: 'Last 15m', durationMs: 15 * 60 * 1000 },
  { value: '1h', label: 'Last 1h', durationMs: 60 * 60 * 1000 },
  { value: '24h', label: 'Last 24h', durationMs: 24 * 60 * 60 * 1000 },
  { value: 'all', label: 'All', durationMs: 0 },
];

const STATUS_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'running', label: 'Running' },
  { value: 'failed', label: 'Failed' },
  { value: 'success', label: 'Succeeded' },
];

const SORT_OPTIONS = [
  { value: 'desc', label: 'Newest first' },
  { value: 'asc', label: 'Oldest first' },
];

const DOCK_HEIGHT = 48;
const MIN_OPEN_HEIGHT = 260;
const MAX_OPEN_HEIGHT = 780;
const MIN_DETAILS_WIDTH = 280;
const MAX_DETAILS_WIDTH = 760;
const MIN_TABLE_WIDTH = 420;

const JOB_COLUMNS = [
  { key: 'status', label: 'Status', minWidth: 110, maxWidth: 220, defaultWidth: 130 },
  { key: 'name', label: 'Job', minWidth: 180, maxWidth: 520, defaultWidth: 240 },
  { key: 'queue', label: 'Queue', minWidth: 140, maxWidth: 400, defaultWidth: 180 },
  { key: 'updated', label: 'Updated', minWidth: 140, maxWidth: 320, defaultWidth: 170 },
  { key: 'duration', label: 'Duration', minWidth: 90, maxWidth: 220, defaultWidth: 110 },
  { key: 'source', label: 'Source', minWidth: 180, maxWidth: 640, defaultWidth: 260 },
  { key: 'error', label: 'Error', minWidth: 180, maxWidth: 700, defaultWidth: 320 },
];

function getDockHeightMax() {
  if (typeof window === 'undefined') {
    return MAX_OPEN_HEIGHT;
  }
  return Math.max(MIN_OPEN_HEIGHT, Math.min(MAX_OPEN_HEIGHT, window.innerHeight - DOCK_HEIGHT - 16));
}

function getInitialDockBodyHeight() {
  if (typeof window === 'undefined') {
    return 520;
  }
  return clamp(Math.round(window.innerHeight * 0.58), MIN_OPEN_HEIGHT, getDockHeightMax());
}

function getDetailsMaxWidth(containerWidth) {
  if (!Number.isFinite(containerWidth) || containerWidth <= 0) {
    return MAX_DETAILS_WIDTH;
  }
  return Math.max(MIN_DETAILS_WIDTH, Math.min(MAX_DETAILS_WIDTH, containerWidth - MIN_TABLE_WIDTH));
}

const JobsDock = () => {
  const { fetchWithAuth, isAuthenticated } = useAuth();
  const { connectionState, lastError, latestJobUpdate } = useRealtime();
  const contentRef = React.useRef(null);
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [jobs, setJobs] = React.useState([]);
  const [selectedJobId, setSelectedJobId] = React.useState(null);
  const [newUpdateCount, setNewUpdateCount] = React.useState(0);

  const [statusFilter, setStatusFilter] = React.useState('all');
  const [queueFilter, setQueueFilter] = React.useState('all');
  const [nameFilter, setNameFilter] = React.useState('all');
  const [timeWindow, setTimeWindow] = React.useState('1h');
  const [sortOrder, setSortOrder] = React.useState('desc');
  const [search, setSearch] = React.useState('');
  const [dockBodyHeight, setDockBodyHeight] = React.useState(getInitialDockBodyHeight);
  const [detailsWidth, setDetailsWidth] = React.useState(360);
  const [relativeNow, setRelativeNow] = React.useState(() => Date.now());
  const { getWidth, startColumnResize, tableMinWidth } = useResizableColumns(JOB_COLUMNS, {
    storageKey: 'table.jobs-dock',
  });

  const beginDrag = React.useCallback((cursor, onMove) => {
    if (typeof window === 'undefined') {
      return;
    }
    const previousUserSelect = document.body.style.userSelect;
    const previousCursor = document.body.style.cursor;
    document.body.style.userSelect = 'none';
    document.body.style.cursor = cursor;

    const handleMove = (event) => {
      onMove(event);
    };

    const handleUp = () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
      document.body.style.userSelect = previousUserSelect;
      document.body.style.cursor = previousCursor;
    };

    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
  }, []);

  const loadJobs = React.useCallback(async () => {
    if (!isAuthenticated) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set('limit', '300');
      params.set('sort', 'desc');
      const response = await fetchWithAuth(`${config.apiBaseUrl}/jobs?${params.toString()}`, {
        timeoutMs: config.longRequestTimeoutMs,
      });
      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.error || 'Failed to load jobs');
      }
      const payload = await response.json();
      const items = Array.isArray(payload?.items) ? payload.items : [];
      setJobs(items);
      if (!selectedJobId && items.length > 0) {
        setSelectedJobId(items[0].id);
      }
    } catch (err) {
      setError(err?.message || 'Failed to load jobs');
    } finally {
      setLoading(false);
    }
  }, [fetchWithAuth, isAuthenticated, selectedJobId]);

  React.useEffect(() => {
    if (!isAuthenticated) {
      setJobs([]);
      setSelectedJobId(null);
      return;
    }
    loadJobs();
  }, [isAuthenticated, loadJobs]);

  React.useEffect(() => {
    if (!isAuthenticated) {
      return undefined;
    }
    const id = window.setInterval(() => {
      loadJobs();
    }, 60 * 1000);
    return () => window.clearInterval(id);
  }, [isAuthenticated, loadJobs]);

  React.useEffect(() => {
    if (!latestJobUpdate || latestJobUpdate.type !== 'job.updated') {
      return;
    }
    const nextJob = latestJobUpdate.job;
    if (!nextJob?.id) {
      return;
    }
    setJobs((prev) => {
      const index = prev.findIndex((job) => job.id === nextJob.id);
      let next;
      if (index === -1) {
        next = [nextJob, ...prev];
      } else {
        next = [...prev];
        next[index] = { ...next[index], ...nextJob };
      }
      next.sort((a, b) => {
        const aTs = Date.parse(a.updated_at || a.started_at || 0);
        const bTs = Date.parse(b.updated_at || b.started_at || 0);
        return bTs - aTs;
      });
      if (next.length > 500) {
        return next.slice(0, 500);
      }
      return next;
    });
    if (!open) {
      setNewUpdateCount((prev) => prev + 1);
    }
  }, [latestJobUpdate, open]);

  React.useEffect(() => {
    if (open) {
      setNewUpdateCount(0);
    }
  }, [open]);

  React.useEffect(() => {
    if (!open) {
      return undefined;
    }
    const id = window.setInterval(() => {
      setRelativeNow(Date.now());
    }, 1000);
    return () => window.clearInterval(id);
  }, [open]);

  React.useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }
    const handleResize = () => {
      setDockBodyHeight((previous) => clamp(previous, MIN_OPEN_HEIGHT, getDockHeightMax()));
      const containerWidth = contentRef.current?.clientWidth ?? window.innerWidth;
      setDetailsWidth((previous) => clamp(previous, MIN_DETAILS_WIDTH, getDetailsMaxWidth(containerWidth)));
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const selectedJob = React.useMemo(
    () => jobs.find((job) => job.id === selectedJobId) || null,
    [jobs, selectedJobId],
  );

  const queueOptions = React.useMemo(() => {
    const set = new Set();
    jobs.forEach((job) => {
      if (job.queue) {
        set.add(job.queue);
      }
    });
    return Array.from(set).sort();
  }, [jobs]);

  const nameOptions = React.useMemo(() => {
    const set = new Set();
    jobs.forEach((job) => {
      if (job.name) {
        set.add(job.name);
      }
    });
    return Array.from(set).sort();
  }, [jobs]);

  const filteredJobs = React.useMemo(() => {
    const now = Date.now();
    const durationMs = TIME_WINDOWS.find((item) => item.value === timeWindow)?.durationMs ?? 0;
    const query = search.trim().toLowerCase();

    const list = jobs.filter((job) => {
      if (statusFilter !== 'all') {
        const status = Number(job.status);
        if (statusFilter === 'running' && status !== 1) return false;
        if (statusFilter === 'success' && status !== 2) return false;
        if (statusFilter === 'failed' && status !== 3) return false;
      }
      if (queueFilter !== 'all' && job.queue !== queueFilter) {
        return false;
      }
      if (nameFilter !== 'all' && job.name !== nameFilter) {
        return false;
      }
      if (durationMs > 0) {
        const ts = Date.parse(job.updated_at || job.started_at || 0);
        if (!Number.isFinite(ts) || now-ts > durationMs) {
          return false;
        }
      }
      if (query) {
        const haystack = `${job.name || ''} ${job.queue || ''} ${job.error || ''} ${job.error_file || ''} ${job.error_line || ''} ${job.error_query || ''} ${job.error_response || ''} ${job.args || ''}`.toLowerCase();
        if (!haystack.includes(query)) {
          return false;
        }
      }
      return true;
    });

    list.sort((a, b) => {
      const aTs = Date.parse(a.updated_at || a.started_at || 0);
      const bTs = Date.parse(b.updated_at || b.started_at || 0);
      if (sortOrder === 'asc') {
        return aTs - bTs;
      }
      return bTs - aTs;
    });
    return list;
  }, [jobs, nameFilter, queueFilter, search, sortOrder, statusFilter, timeWindow]);

  const runningCount = React.useMemo(() => jobs.filter((job) => Number(job.status) === 1).length, [jobs]);
  const failedCount = React.useMemo(() => jobs.filter((job) => Number(job.status) === 3).length, [jobs]);
  const successCount = React.useMemo(() => jobs.filter((job) => Number(job.status) === 2).length, [jobs]);

  const startDockResize = React.useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    const startY = event.clientY;
    const startHeight = dockBodyHeight;
    beginDrag('row-resize', (moveEvent) => {
      const nextHeight = startHeight - (moveEvent.clientY - startY);
      setDockBodyHeight(clamp(nextHeight, MIN_OPEN_HEIGHT, getDockHeightMax()));
    });
  }, [beginDrag, dockBodyHeight]);

  const resizeDockBy = React.useCallback((delta) => {
    setDockBodyHeight((previous) => clamp(previous + delta, MIN_OPEN_HEIGHT, getDockHeightMax()));
  }, []);

  const resizeDockTo = React.useCallback((height) => {
    setDockBodyHeight(clamp(height, MIN_OPEN_HEIGHT, getDockHeightMax()));
  }, []);

  const startDetailsResize = React.useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    const startX = event.clientX;
    const startWidth = detailsWidth;
    beginDrag('col-resize', (moveEvent) => {
      const containerWidth = contentRef.current?.clientWidth ?? window.innerWidth;
      const nextWidth = startWidth - (moveEvent.clientX - startX);
      setDetailsWidth(clamp(nextWidth, MIN_DETAILS_WIDTH, getDetailsMaxWidth(containerWidth)));
    });
  }, [beginDrag, detailsWidth]);

  const resizeDetailsBy = React.useCallback((delta) => {
    const containerWidth = contentRef.current?.clientWidth ?? window.innerWidth;
    setDetailsWidth((previous) => clamp(previous + delta, MIN_DETAILS_WIDTH, getDetailsMaxWidth(containerWidth)));
  }, []);

  const resizeDetailsTo = React.useCallback((width) => {
    const containerWidth = contentRef.current?.clientWidth ?? window.innerWidth;
    setDetailsWidth(clamp(width, MIN_DETAILS_WIDTH, getDetailsMaxWidth(containerWidth)));
  }, []);

  const toggleDock = React.useCallback(() => {
    setOpen((prev) => !prev);
  }, []);

  const handleDockToggleKeyDown = React.useCallback(
    (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
      toggleDock();
    },
    [toggleDock],
  );

  const handleDockResizeKeyDown = React.useCallback(
    (event) => {
      const step = event.shiftKey ? 64 : 16;
      switch (event.key) {
        case 'ArrowUp':
          event.preventDefault();
          resizeDockBy(step);
          break;
        case 'ArrowDown':
          event.preventDefault();
          resizeDockBy(-step);
          break;
        case 'Home':
          event.preventDefault();
          resizeDockTo(MIN_OPEN_HEIGHT);
          break;
        case 'End':
          event.preventDefault();
          resizeDockTo(getDockHeightMax());
          break;
        default:
          break;
      }
    },
    [resizeDockBy, resizeDockTo],
  );

  const handleDetailsResizeKeyDown = React.useCallback(
    (event) => {
      const step = event.shiftKey ? 64 : 16;
      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault();
          resizeDetailsBy(step);
          break;
        case 'ArrowRight':
          event.preventDefault();
          resizeDetailsBy(-step);
          break;
        case 'Home':
          event.preventDefault();
          resizeDetailsTo(MIN_DETAILS_WIDTH);
          break;
        case 'End':
          event.preventDefault();
          resizeDetailsTo(getDetailsMaxWidth(contentRef.current?.clientWidth ?? window.innerWidth));
          break;
        default:
          break;
      }
    },
    [resizeDetailsBy, resizeDetailsTo],
  );

  const connectionChip = React.useMemo(() => {
    if (connectionState === 'connected') {
      return <Chip size="small" color="success" label="Live" />;
    }
    if (connectionState === 'connecting') {
      return <Chip size="small" color="warning" label="Connecting" />;
    }
    if (connectionState === 'error') {
      return <Chip size="small" color="error" label="Offline" />;
    }
    return <Chip size="small" label="Idle" />;
  }, [connectionState]);

  return (
    <Box
      sx={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: (theme) => theme.zIndex.drawer + 20,
        pointerEvents: 'none',
      }}
    >
      <Paper
        elevation={8}
        sx={{
          pointerEvents: 'auto',
          borderRadius: 0,
          borderTop: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
        }}
      >
        <Box
          role="button"
          tabIndex={0}
          aria-expanded={open}
          aria-controls="jobs-dock-panel"
          aria-label={open ? 'Collapse Team Jobs' : 'Expand Team Jobs'}
          onClick={toggleDock}
          onKeyDown={handleDockToggleKeyDown}
          sx={{
            height: DOCK_HEIGHT,
            px: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
            backgroundColor: 'background.paper',
            userSelect: 'none',
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            {open ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Team Jobs
            </Typography>
            {connectionChip}
            <Chip icon={<PlayCircleOutlineIcon />} size="small" label={`Running ${runningCount}`} />
            <Chip icon={<ErrorOutlineIcon />} size="small" color={failedCount ? 'error' : 'default'} label={`Failed ${failedCount}`} />
            <Chip icon={<CheckCircleOutlineIcon />} size="small" color={successCount ? 'success' : 'default'} label={`Succeeded ${successCount}`} />
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            {!open && newUpdateCount > 0 ? (
              <Badge color="primary" badgeContent={newUpdateCount}>
                <Typography variant="caption" color="text.secondary">
                  New updates
                </Typography>
              </Badge>
            ) : null}
            <Tooltip title="Refresh jobs">
              <IconButton
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  loadJobs();
                }}
                aria-label="Refresh jobs"
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>

        {open ? (
          <Box
            id="jobs-dock-panel"
            sx={{
              height: dockBodyHeight,
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: 'background.default',
              borderTop: '1px solid',
              borderColor: 'divider',
              position: 'relative',
            }}
          >
            <Box
              role="separator"
              tabIndex={0}
              aria-label="Resize Team Jobs panel"
              aria-orientation="horizontal"
              aria-valuemin={MIN_OPEN_HEIGHT}
              aria-valuemax={getDockHeightMax()}
              aria-valuenow={Math.round(dockBodyHeight)}
              aria-controls="jobs-dock-panel"
              onClick={(event) => event.stopPropagation()}
              onMouseDown={startDockResize}
              onKeyDown={handleDockResizeKeyDown}
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: 8,
                cursor: 'row-resize',
                zIndex: 3,
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
                '&:focus-visible': {
                  backgroundColor: 'action.focus',
                  outline: (theme) => `2px solid ${theme.palette.primary.main}`,
                  outlineOffset: -2,
                },
              }}
            />
            {error ? (
              <Alert severity="error" sx={{ borderRadius: 0 }}>
                {error}
              </Alert>
            ) : null}
            {lastError && connectionState === 'error' ? (
              <Alert severity="warning" sx={{ borderRadius: 0 }}>
                Realtime disconnected: {lastError}
              </Alert>
            ) : null}

            <Stack direction="row" spacing={1} sx={{ p: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
              <FormControl size="small" sx={{ minWidth: 130 }}>
                <InputLabel>Status</InputLabel>
                <Select value={statusFilter} label="Status" onChange={(event) => setStatusFilter(event.target.value)}>
                  {STATUS_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Queue</InputLabel>
                <Select value={queueFilter} label="Queue" onChange={(event) => setQueueFilter(event.target.value)}>
                  <MenuItem value="all">All</MenuItem>
                  {queueOptions.map((queue) => (
                    <MenuItem key={queue} value={queue}>
                      {queue}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 190 }}>
                <InputLabel>Job</InputLabel>
                <Select value={nameFilter} label="Job" onChange={(event) => setNameFilter(event.target.value)}>
                  <MenuItem value="all">All</MenuItem>
                  {nameOptions.map((name) => (
                    <MenuItem key={name} value={name}>
                      {name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 130 }}>
                <InputLabel>Window</InputLabel>
                <Select value={timeWindow} label="Window" onChange={(event) => setTimeWindow(event.target.value)}>
                  {TIME_WINDOWS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Sort</InputLabel>
                <Select value={sortOrder} label="Sort" onChange={(event) => setSortOrder(event.target.value)}>
                  {SORT_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                size="small"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                label="Search jobs"
                sx={{ minWidth: 240, flex: 1 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>

            <Divider />

            <Box ref={contentRef} sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
              <TableContainer sx={{ flex: 1, minWidth: MIN_TABLE_WIDTH }}>
                <Table size="small" stickyHeader sx={{ tableLayout: 'fixed', minWidth: tableMinWidth }}>
                  <ResizableColGroup columns={JOB_COLUMNS} getWidth={getWidth} />
                  <TableHead>
                    <TableRow>
                      {JOB_COLUMNS.map((column) => (
                        <ResizableHeaderCell
                          key={column.key}
                          column={column}
                          getWidth={getWidth}
                          onResizeStart={startColumnResize}
                        />
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      <TableLoadingRow colSpan={JOB_COLUMNS.length} label="Loading jobs..." />
                    ) : filteredJobs.length === 0 ? (
                      <TableEmptyRow colSpan={JOB_COLUMNS.length} label="No jobs match your filters." />
                    ) : (
                      filteredJobs.map((job) => {
                        const selected = selectedJobId === job.id;
                        return (
                          <TableRow
                            key={job.id}
                            hover
                            selected={selected}
                            onClick={() => setSelectedJobId(job.id)}
                            sx={{ cursor: 'pointer' }}
                          >
                            <TableCell>{renderJobStatusChip(job.status)}</TableCell>
                            <TableCell>{job.name || '-'}</TableCell>
                            <TableCell>{job.queue || '-'}</TableCell>
                            <TableCell>
                              <Tooltip title={formatTimestamp(job.updated_at)}>
                                <Typography variant="body2">{formatRelative(job.updated_at, relativeNow)}</Typography>
                              </Tooltip>
                            </TableCell>
                            <TableCell>{job.seconds ? `${job.seconds}s` : '-'}</TableCell>
                            <TableCell>
                              {job.error_file ? (
                                <Tooltip title={formatJobSource(job, false)}>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      maxWidth: Math.max(120, getWidth('source') - 24),
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      fontFamily: 'monospace',
                                    }}
                                  >
                                    {formatJobSource(job, true)}
                                  </Typography>
                                </Tooltip>
                              ) : (
                                '-'
                              )}
                            </TableCell>
                            <TableCell>
                              {job.error ? (
                                <Tooltip title={job.error}>
                                  <Typography
                                    variant="body2"
                                    color="error.main"
                                    sx={{
                                      maxWidth: Math.max(120, getWidth('error') - 24),
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {job.error}
                                  </Typography>
                                </Tooltip>
                              ) : (
                                '-'
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box
                role="separator"
                tabIndex={0}
                aria-label="Resize job details panel"
                aria-orientation="vertical"
                aria-valuemin={MIN_DETAILS_WIDTH}
                aria-valuemax={getDetailsMaxWidth(contentRef.current?.clientWidth ?? window.innerWidth)}
                aria-valuenow={Math.round(detailsWidth)}
                onClick={(event) => event.stopPropagation()}
                onMouseDown={startDetailsResize}
                onKeyDown={handleDetailsResizeKeyDown}
                sx={{
                  width: 10,
                  flexShrink: 0,
                  cursor: 'col-resize',
                  position: 'relative',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    left: '50%',
                    width: 1,
                    backgroundColor: 'divider',
                    transform: 'translateX(-50%)',
                  },
                  '&:hover::before': {
                    backgroundColor: 'primary.main',
                  },
                  '&:focus-visible': {
                    outline: (theme) => `2px solid ${theme.palette.primary.main}`,
                    outlineOffset: -2,
                  },
                  '&:focus-visible::before': {
                    backgroundColor: 'primary.main',
                  },
                }}
              />

              <Box sx={{ width: detailsWidth, p: 1.5, overflow: 'auto', flexShrink: 0 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Job Details
                </Typography>
                {selectedJob ? (
                  <Stack spacing={1.2}>
                    <Typography variant="body2">
                      <strong>ID:</strong> {selectedJob.id}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Name:</strong> {selectedJob.name || '-'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Queue:</strong> {selectedJob.queue || '-'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Status:</strong> {renderJobStatusLabel(selectedJob.status)}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Started:</strong> {formatTimestamp(selectedJob.started_at)}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Updated:</strong> {formatTimestamp(selectedJob.updated_at)}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Duration:</strong> {selectedJob.seconds ? `${selectedJob.seconds}s` : '-'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Error Source:</strong> {formatJobSource(selectedJob, false)}
                    </Typography>
                    {selectedJob.error ? (
                      <Alert severity="error">{selectedJob.error}</Alert>
                    ) : null}
                    {selectedJob.error_query ? (
                      <>
                        <Typography variant="caption" color="text.secondary">
                          SQL Query
                        </Typography>
                        <Box
                          component="pre"
                          sx={{
                            m: 0,
                            p: 1.2,
                            borderRadius: 1,
                            backgroundColor: 'grey.100',
                            maxHeight: 180,
                            overflow: 'auto',
                            fontSize: 12,
                            lineHeight: 1.4,
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                          }}
                        >
                          {selectedJob.error_query}
                        </Box>
                      </>
                    ) : null}
                    {selectedJob.error_response ? (
                      <>
                        <Typography variant="caption" color="text.secondary">
                          API Response
                        </Typography>
                        <Box
                          component="pre"
                          sx={{
                            m: 0,
                            p: 1.2,
                            borderRadius: 1,
                            backgroundColor: 'grey.100',
                            maxHeight: 180,
                            overflow: 'auto',
                            fontSize: 12,
                            lineHeight: 1.4,
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                          }}
                        >
                          {selectedJob.error_response}
                        </Box>
                      </>
                    ) : null}
                    <Typography variant="caption" color="text.secondary">
                      Args
                    </Typography>
                    <Box
                      component="pre"
                      sx={{
                        m: 0,
                        p: 1.2,
                        borderRadius: 1,
                        backgroundColor: 'grey.100',
                        maxHeight: 220,
                        overflow: 'auto',
                        fontSize: 12,
                        lineHeight: 1.4,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}
                    >
                      {prettyJSON(selectedJob.args)}
                    </Box>
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Select a job row to view details.
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        ) : null}
      </Paper>
    </Box>
  );
};

function renderJobStatusChip(status) {
  const numeric = Number(status);
  if (numeric === 1) {
    return <Chip size="small" color="warning" label="Running" icon={<SyncIcon />} />;
  }
  if (numeric === 2) {
    return <Chip size="small" color="success" label="Succeeded" />;
  }
  if (numeric === 3) {
    return <Chip size="small" color="error" label="Failed" />;
  }
  return <Chip size="small" label={`Status ${status}`} />;
}

function renderJobStatusLabel(status) {
  const numeric = Number(status);
  if (numeric === 1) return 'Running';
  if (numeric === 2) return 'Succeeded';
  if (numeric === 3) return 'Failed';
  return String(status || '-');
}

function formatTimestamp(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString();
}

function formatRelative(value, nowTs = Date.now()) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  const deltaMs = nowTs - date.getTime();
  const deltaSec = Math.round(deltaMs / 1000);
  if (Math.abs(deltaSec) < 60) return `${deltaSec}s ago`;
  const deltaMin = Math.round(deltaSec / 60);
  if (Math.abs(deltaMin) < 60) return `${deltaMin}m ago`;
  const deltaHr = Math.round(deltaMin / 60);
  if (Math.abs(deltaHr) < 24) return `${deltaHr}h ago`;
  const deltaDay = Math.round(deltaHr / 24);
  return `${deltaDay}d ago`;
}

function prettyJSON(raw) {
  if (!raw) return '-';
  try {
    const parsed = JSON.parse(raw);
    return JSON.stringify(parsed, null, 2);
  } catch (err) {
    return String(raw);
  }
}

function formatJobSource(job, compact) {
  const file = String(job?.error_file || '').trim();
  const line = Number(job?.error_line || 0);
  if (!file) {
    return '-';
  }
  if (!compact) {
    return line > 0 ? `${file}:${line}` : file;
  }
  const base = file.split('/').pop() || file;
  return line > 0 ? `${base}:${line}` : base;
}

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

export default JobsDock;
